import { maskitoTransform } from "@maskito/core";
import { useMaskito } from "@maskito/react";
import { CurrencyDollarIcon } from "@phosphor-icons/react/dist/csr/CurrencyDollar";
import { type SyntheticEvent, useMemo, useState } from "react";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import { Field, FieldError } from "@volca/ui/components/ui/field";
import { Input } from "@volca/ui/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@volca/ui/components/ui/input-group";
import { getValidDollarAmountMask, validPercentageMask } from "@volca/ui/masks";
import {
  PAYROLL_ROSTER_FIELD_FORMAT,
  PAYROLL_ROSTER_FIELD_TYPE,
  type PayrollRosterField,
  type PayrollRosterValue,
} from "./types";
import { humanizeRosterOption } from "./utils";
import { maskNumericValue } from "./utils";

interface MaskedNumberInputProps {
  // Only the display-formatting number types route here (currency / percent).
  format: "currency" | "percent";
  ariaLabel: string;
  // The STORED plain numeric string (no $, commas, or %).
  value: string;
  error?: string;
  disabled?: boolean;
  onChange: (plainValue: string) => void;
}

// A number input that displays the app's standard money/percent mask (via
// maskito) while storing a PLAIN numeric string. The masked text is display-
// only: on load the stored plain number is run through the mask; on change the
// masked text is stripped back to a plain number for the draft / API (which
// validates Float(value) and rejects $, commas, and %).
const MaskedNumberInput = ({
  format,
  ariaLabel,
  value,
  error,
  disabled,
  onChange,
}: MaskedNumberInputProps) => {
  const maskOptions = useMemo(
    () =>
      format === PAYROLL_ROSTER_FIELD_FORMAT.currency
        ? getValidDollarAmountMask(false)
        : validPercentageMask,
    [format],
  );
  const maskitoRef = useMaskito({ options: maskOptions });
  // Local masked display, seeded from the stored plain number through the mask.
  // Blank stays blank (so an empty percent field doesn't render a stray "%"),
  // and a real number is canonicalized first so trailing zeros drop ("46.0" ->
  // "46", "5.50" -> "5.5") to match how the table's formatMoney renders it.
  const [display, setDisplay] = useState(() => {
    if (!value) return "";
    const numeric = Number(value);
    const seed = Number.isFinite(numeric) ? String(numeric) : value;
    return maskitoTransform(seed, maskOptions);
  });

  // Maskito rewrites the input value imperatively via its ref, which desyncs
  // React's controlled-value tracker and can swallow the synthetic `onChange`.
  // Read the maskito-processed value off the native `input` event (which always
  // fires) so the typed value actually reaches state — mirrors MaskedFormInput.
  const handleValue = (event: SyntheticEvent<HTMLInputElement>) => {
    const next = event.currentTarget.value;
    setDisplay(next);
    onChange(maskNumericValue(next));
  };

  return (
    <Field data-invalid={Boolean(error) || undefined}>
      <InputGroup>
        {format === PAYROLL_ROSTER_FIELD_FORMAT.currency && (
          <InputGroupAddon align="inline-start">
            <CurrencyDollarIcon />
          </InputGroupAddon>
        )}
        <InputGroupInput
          ref={maskitoRef}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          disabled={disabled}
          inputMode="decimal"
          value={display}
          onInput={handleValue}
          onChange={handleValue}
        />
      </InputGroup>
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
};

interface RosterFieldControlProps {
  field: PayrollRosterField;
  // fixed_option choices sourced live from the contract (falls back to the
  // field's stored options only when the business is ungoverned).
  options: string[];
  value: PayrollRosterValue | undefined;
  error?: string;
  disabled?: boolean;
  onChange: (value: PayrollRosterValue) => void;
}

// The right control for one roster field inside the edit drawer. fixed_option
// and boolean fields use a type-to-filter Autocomplete; number/text reuse the
// design-system Input so height/width match the Autocomplete exactly.
export const RosterFieldControl = ({
  field,
  options,
  value,
  error,
  disabled,
  onChange,
}: RosterFieldControlProps) => {
  const initialSearchValue =
    field.field_type === PAYROLL_ROSTER_FIELD_TYPE.boolean
      ? value === true
        ? "Yes"
        : value === false
          ? "No"
          : ""
      : field.field_type === PAYROLL_ROSTER_FIELD_TYPE.fixedOption &&
          typeof value === "string"
        ? humanizeRosterOption(value)
        : typeof value === "string"
          ? value
          : "";
  const [searchValue, setSearchValue] = useState(initialSearchValue);

  if (field.field_type === PAYROLL_ROSTER_FIELD_TYPE.fixedOption) {
    const selected = typeof value === "string" ? value : "";
    const query = searchValue.toLowerCase();
    const items = options
      .map((option) => ({ value: option, label: humanizeRosterOption(option) }))
      // Match what is shown as well as what is stored, so typing "service tech"
      // finds `service_tech`.
      .filter(
        ({ value, label }) =>
          value.toLowerCase().includes(query) ||
          label.toLowerCase().includes(query),
      );

    return (
      <AutoComplete
        fullWidth
        ariaLabel={field.label}
        disabled={disabled}
        selectedValue={selected}
        onSelectedValueChange={(next) => onChange(next === "" ? null : next)}
        searchValue={searchValue}
        onSearchValueChange={setSearchValue}
        items={items}
        placeholder={`Search ${field.label.toLowerCase()}`}
        emptyMessage={`No ${field.label.toLowerCase()} options`}
        error={error}
      />
    );
  }

  if (field.field_type === PAYROLL_ROSTER_FIELD_TYPE.boolean) {
    const selected = value === true ? "true" : value === false ? "false" : "";
    const items = [
      { value: "true", label: "Yes" },
      { value: "false", label: "No" },
    ].filter((item) =>
      item.label.toLowerCase().includes(searchValue.toLowerCase()),
    );

    return (
      <AutoComplete
        fullWidth
        ariaLabel={field.label}
        disabled={disabled}
        selectedValue={selected}
        onSelectedValueChange={(next) =>
          onChange(next === "" ? null : next === "true")
        }
        searchValue={searchValue}
        onSearchValueChange={setSearchValue}
        items={items}
        placeholder={`Select ${field.label.toLowerCase()}`}
        emptyMessage="No options"
        error={error}
      />
    );
  }

  const isNumber = field.field_type === PAYROLL_ROSTER_FIELD_TYPE.number;
  const stringValue = typeof value === "string" ? value : "";

  // Money / percent numbers use the app's standard maskito masks (thousand
  // separators + $ icon, or a % suffix), storing a plain number.
  if (
    isNumber &&
    (field.format === PAYROLL_ROSTER_FIELD_FORMAT.currency ||
      field.format === PAYROLL_ROSTER_FIELD_FORMAT.percent)
  ) {
    return (
      <MaskedNumberInput
        format={field.format}
        ariaLabel={field.label}
        value={stringValue}
        error={error}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }

  // Plain numbers strip to digits + a single decimal point; text is free-form.
  return (
    <Field data-invalid={Boolean(error) || undefined}>
      <Input
        aria-label={field.label}
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        inputMode={isNumber ? "decimal" : undefined}
        value={stringValue}
        onChange={(event) =>
          onChange(
            isNumber
              ? maskNumericValue(event.target.value)
              : event.target.value,
          )
        }
      />
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
};
